import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Building2, BarChart3, Zap, Shield, Map, ArrowUpRight, Check } from "lucide-react";
import { IMG } from "@/lib/images";
import PageBanner from "@/components/common/PageBanner";

const SOLUTIONS = [
  { icon: Brain, title: "AI Media Planning", body: "Frontier LLMs analyse your brief and produce a buyable plan — malls, formats, budget split, expected reach, KPIs and creative directions.", points: ["City-weight recommendations", "Mall-level selections", "Format mix optimisation", "Explainable reasoning"] },
  { icon: Building2, title: "Mall Marketplace", body: "A curated, unified inventory of India&apos;s most premium malls — searchable by city, format, audience, footfall, visibility and budget.", points: ["120+ premium malls", "85+ ad formats", "Tier 1 & Tier 2 cities", "Live availability"] },
  { icon: Zap, title: "Experiential Activations", body: "Sampling, kiosks, vehicle reveals, immersive zones — designed, fabricated and operated end-to-end.", points: ["Pan-India ops", "Concept to creative", "On-ground manpower", "Daily reporting"] },
  { icon: BarChart3, title: "Retail Media Analytics", body: "Footfall, dwell, demographics, visibility scoring, QR scans, redemptions, brand-lift studies.", points: ["Footfall + dwell time", "Catchment demographics", "Attribution windows", "Brand-lift studies"] },
  { icon: Map, title: "Pan-India Coverage", body: "Tier-1 dominance, Tier-2 expansion. Every premium catchment from Mumbai to Calicut.", points: ["45+ cities", "Metro + Tier-2", "Atrium-grade malls", "Local language support"] },
  { icon: Shield, title: "Enterprise SLAs", body: "Vetted partners, transparent pricing, signed quotes, creative approvals, dashboards.", points: ["Signed quotes", "Creative approval flow", "Campaign tracking", "Centralised invoicing"] },
];

export default function Solutions() {
  return (
    <div data-testid="solutions-page" className="bg-[var(--bone)]">
      <PageBanner
        eyebrow="Solutions"
        title="A full retail-media stack —"
        italicWord="in one platform."
        kicker="Fundle combines mall inventory, AI planning, experiential operations and analytics into a single workflow."
        image={IMG.hangingBanner}
      />
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
        {SOLUTIONS.map((s, idx) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: idx * 0.05 }}
            className="bg-[var(--bone)] p-8 lg:p-10"
            data-testid={`solution-${idx}`}
          >
            <s.icon size={24} className="text-[var(--gold)]" strokeWidth={1.5} />
            <h3 className="mt-6 font-serif-display text-2xl text-[var(--ink)]">{s.title}</h3>
            <p className="mt-3 text-[var(--slate-1)] text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: s.body }} />
            <ul className="mt-5 space-y-2">
              {s.points.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-[var(--ink)]">
                  <Check size={14} className="text-[var(--gold)]" /> {p}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </section>

      <section className="bg-[var(--obsidian)] text-[var(--bone)] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <h2 className="font-serif-display text-4xl lg:text-6xl text-white">
            Bring your brief. <span className="font-serif-italic text-[var(--gold-light)]">We&apos;ll bring the malls.</span>
          </h2>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/ai-planner" className="btn-primary !bg-[var(--gold)] !border-[var(--gold)] !text-white hover:!bg-white hover:!text-[var(--obsidian)] hover:!border-white">Plan with AI</Link>
            <Link to="/contact" className="btn-secondary !text-white !border-white hover:!bg-white hover:!text-[var(--obsidian)]">Talk to a strategist <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
